// Read-only client access to casual ratings + leaderboard (Supabase). The
// client never WRITES ratings — those are computed and persisted server-side
// (the Durable Objects backend via the service role). All reads degrade
// gracefully to null/[] when auth/the backend isn't configured or the tables
// don't exist yet, so nothing here can crash a page that calls it early.
import { supabase } from "../supabase";
import type { PlayerRating } from "./rating";

export interface RatingRow {
  agentId: string;
  displayName: string;
  rating: PlayerRating; // raw OpenSkill {mu, sigma}
  displayRating: number; // precomputed display number
  games: number;
  wins: number;
  podiums: number;
  bestPlace: number | null;
  peak: number;
  // Average share of the field beaten, 0-100 (see LeaderboardEntry.placementPct).
  placementPct: number | null;
  // Lifetime fun-stat totals (accumulated by the edge-server per rated match;
  // see on-match-over.accumulateLifetimeStats for the shape). null before
  // migration 0006 / before the first stat-carrying match.
  lifetimeStats: any | null;
}

export interface LeaderboardEntry {
  position: number;
  agentId: string;
  displayName: string;
  rating: number;
  games: number;
  wins: number;
  podiums: number;
  bestPlace: number | null;
  // Average share of the field beaten, 0-100 (100 = wins every game; 50 =
  // mid-field). null when the DB doesn't have the column yet (migration 0003).
  placementPct: number | null;
  // The flag this player flew in their last rated match (ISO or novelty code).
  // null before migration 0012, or until they play again after it.
  flag: string | null;
}

// One agent's rating row (e.g. the signed-in user). null = no row / no backend.
export async function fetchRating(agentId: string): Promise<RatingRow | null> {
  if (!supabase) return null;
  try {
    // The two selects return different generated row types, so type the slot
    // loosely: the mapping below is the real contract.
    let { data, error } = (await supabase
      .from("casual_ratings")
      .select("agent_id, display_name, mu, sigma, rating, games, wins, podiums, best_place, peak_rating, place_score, lifetime_stats")
      .eq("agent_id", agentId)
      .maybeSingle()) as { data: any; error: { code?: string } | null };
    if (error && error.code === "42703") {
      // lifetime_stats doesn't exist yet (migration 0006 not applied): retry
      // keeping place_score, so Place % survives a partial migration state.
      ({ data, error } = (await supabase
        .from("casual_ratings")
        .select("agent_id, display_name, mu, sigma, rating, games, wins, podiums, best_place, peak_rating, place_score")
        .eq("agent_id", agentId)
        .maybeSingle()) as { data: any; error: { code?: string } | null });
    }
    if (error && error.code === "42703") {
      // place_score doesn't exist either (migration 0003 not applied): degrade
      // to the launch columns.
      ({ data, error } = (await supabase
        .from("casual_ratings")
        .select("agent_id, display_name, mu, sigma, rating, games, wins, podiums, best_place, peak_rating")
        .eq("agent_id", agentId)
        .maybeSingle()) as { data: any; error: { code?: string } | null });
    }
    if (!error && !data) {
      // The account genuinely has no rating row: forget any cached one so a
      // reset player doesn't keep flashing an old rank.
      clearCachedRating(agentId);
      return null;
    }
    if (error || !data) return null;
    const row: RatingRow = {
      agentId: data.agent_id,
      displayName: data.display_name,
      rating: { mu: data.mu, sigma: data.sigma },
      displayRating: data.rating,
      games: data.games,
      wins: data.wins,
      podiums: data.podiums,
      bestPlace: data.best_place,
      peak: data.peak_rating,
      placementPct: (data as any).place_score == null ? null : data.games ? Math.round((100 * (data as any).place_score) / data.games) : 0,
      lifetimeStats: (data as any).lifetime_stats ?? null,
    };
    cacheRating(row);
    return row;
  } catch {
    return null;
  }
}

// Last-known rating per agent (memory + localStorage), so screens can seed
// their state and show the right insignia on first paint instead of flashing
// the unranked egg for the fetch round-trip.
const RATING_CACHE_PREFIX = "ar.rating.";
const ratingCache = new Map<string, RatingRow>();

function cacheRating(row: RatingRow) {
  ratingCache.set(row.agentId, row);
  try {
    localStorage.setItem(RATING_CACHE_PREFIX + row.agentId, JSON.stringify(row));
  } catch {
    // Storage full/blocked: the in-memory cache still covers this session.
  }
}

function clearCachedRating(agentId: string) {
  ratingCache.delete(agentId);
  try {
    localStorage.removeItem(RATING_CACHE_PREFIX + agentId);
  } catch {
    // ignore
  }
}

export function cachedRating(agentId: string): RatingRow | null {
  const hit = ratingCache.get(agentId);
  if (hit) return hit;
  try {
    const raw = localStorage.getItem(RATING_CACHE_PREFIX + agentId);
    if (!raw) return null;
    const row = JSON.parse(raw) as RatingRow;
    ratingCache.set(agentId, row);
    return row;
  } catch {
    return null;
  }
}

// One row of a player's match history (casual_match_results), oldest first.
export interface MatchHistoryRow {
  placement: number;
  fieldSize: number;
  map: string | null;
  playstyle: string | null; // recorded from 2026-08-20 on; null before that
  muBefore: number;
  muAfter: number;
  createdAt: string;
  matchId: string | null; // the server's match id: what a replay is stored under
}

// A player's latest rated matches, `limit` of them, oldest first (own profile /
// any human agent; bot rows are RLS-hidden). The newest are the ones kept: a
// player past `limit` matches sees their recent games, not their first ones.
// null = couldn't load; [] = no matches yet. select("*") keeps this tolerant of
// columns that newer migrations add.
export async function fetchMatchHistory(agentId: string, limit = 300): Promise<MatchHistoryRow[] | null> {
  if (!supabase) return null;
  try {
    const { data, error } = await supabase
      .from("casual_match_results")
      .select("*")
      .eq("agent_id", agentId)
      .order("created_at", { ascending: false })
      .limit(limit);
    if (error) return null;
    return (data || []).reverse().map((d: any) => ({
      placement: d.placement,
      fieldSize: d.field_size,
      map: d.map ?? null,
      playstyle: d.playstyle ?? null,
      muBefore: d.mu_before,
      muAfter: d.mu_after,
      createdAt: d.created_at,
      matchId: d.match_id ?? null,
    }));
  } catch {
    return null;
  }
}

// Top of the public leaderboard. [] = genuinely empty; null = couldn't load
// (no backend configured, network/API error) so the page can show an error
// state instead of a misleading "no ranked players yet".
const LB_COLS = "position, agent_id, display_name, rating, games, wins, podiums, best_place, placement_pct, flag";
// Deploy-order tolerance, newest column first: flag arrives with migration
// 0012, placement_pct with 0003. Each tier drops the one the DB lacks so the
// rows still load and the missing cell renders empty until it catches up.
const LB_COLS_NO_FLAG = "position, agent_id, display_name, rating, games, wins, podiums, best_place, placement_pct";
const LB_COLS_LEGACY = "position, agent_id, display_name, rating, games, wins, podiums, best_place";

function mapLeaderboardRow(d: any): LeaderboardEntry {
  return {
    position: d.position,
    agentId: d.agent_id,
    displayName: d.display_name,
    rating: d.rating,
    games: d.games,
    wins: d.wins,
    podiums: d.podiums,
    bestPlace: d.best_place,
    placementPct: d.placement_pct ?? null,
    flag: d.flag ?? null,
  };
}

// Leaderboard filters. `humansOnly` is an ADMIN-ONLY view (the leaderboard
// page's "no bots" switch, gated by isAdmin): the public leaderboard view masks
// bot ids to "p_<md5>" (migration 0002) while every human id is a Supabase auth
// uuid, so "not like p_%" leaves exactly the real players. Positions stay the
// public ones (a human who is #7 overall still shows as #7). Never expose this
// to players: the bots are a secret.
export interface LeaderboardFilter {
  humansOnly?: boolean;
}
const MASKED_BOT_PREFIX = "p_";

// One leaderboard query; `shape` adds the filters / ordering / limit. The two
// column lists return different generated row types, so the builder is typed
// loosely: mapLeaderboardRow is the real contract.
async function queryLeaderboard(shape: (q: any) => any, filter: LeaderboardFilter = {}): Promise<LeaderboardEntry[] | null> {
  if (!supabase) return null;
  const withFilter = (q: any) => (filter.humansOnly ? q.not("agent_id", "like", MASKED_BOT_PREFIX + "%") : q);
  try {
    type R = { data: any[] | null; error: { code?: string } | null };
    let { data, error } = (await shape(withFilter(supabase.from("casual_leaderboard").select(LB_COLS)))) as R;
    // 42703 = undefined column: step down a tier at a time.
    if (error && error.code === "42703") ({ data, error } = (await shape(withFilter(supabase.from("casual_leaderboard").select(LB_COLS_NO_FLAG)))) as R);
    if (error && error.code === "42703") ({ data, error } = (await shape(withFilter(supabase.from("casual_leaderboard").select(LB_COLS_LEGACY)))) as R);
    if (error) return null;
    return (data ?? []).map(mapLeaderboardRow);
  } catch {
    return null;
  }
}

export async function fetchLeaderboard(limit = 100, filter: LeaderboardFilter = {}): Promise<LeaderboardEntry[] | null> {
  return queryLeaderboard((q) => q.order("position", { ascending: true }).limit(limit), filter);
}

// One player's public leaderboard row (position + the public stats). This is
// the one read that works for EVERY ranked agent, bots included (the view
// masks their ids, the base tables hide them): it is what a public profile
// falls back on when the ratings table has no readable row. null = no row or
// couldn't load.
export async function fetchLeaderboardEntry(agentId: string): Promise<LeaderboardEntry | null> {
  const rows = await queryLeaderboardEntry(agentId);
  return rows && rows.length ? rows[0] : null;
}
// The raw one-row query: null = couldn't load, [] = no row (the callers that
// tell the two apart use this).
function queryLeaderboardEntry(agentId: string): Promise<LeaderboardEntry[] | null> {
  return queryLeaderboard((q) => q.eq("agent_id", agentId).limit(1));
}

// A player's position on the public leaderboard (1 = top). null = no row or
// couldn't load.
export async function fetchLeaderboardPosition(agentId: string): Promise<number | null> {
  const mine = await fetchLeaderboardEntry(agentId);
  return mine ? mine.position : null;
}

// The rows around one player: their own row plus `span` positions either side,
// for showing where a player ranked below the visible top actually sits.
// null = couldn't load; [] = the player has no leaderboard row.
export async function fetchLeaderboardAround(agentId: string, span = 5, filter: LeaderboardFilter = {}): Promise<LeaderboardEntry[] | null> {
  const mine = await queryLeaderboardEntry(agentId);
  if (!mine) return null;
  if (!mine.length) return [];
  const p = mine[0].position;
  return queryLeaderboard((q) => q.gte("position", Math.max(1, p - span)).lte("position", p + span).order("position", { ascending: true }), filter);
}


// ---------------------------------------------------------------------------
// The ranked ladders (migration 0013): 1v1 ("duel") and six-player free-for-all
// ("ffa"), one pair of tables keyed by mode, the same shapes as casual. A ladder
// shows no rank until RANKED_PLACEMENT_GAMES matches are in.
// ---------------------------------------------------------------------------

export type RankedMode = "duel" | "ffa";
export const RANKED_MODES: RankedMode[] = ["duel", "ffa"];
export const RANKED_PLACEMENT_GAMES = 5;

// A ranked row, plus which ladder it is on. `placed` is games >= placements.
export interface RankedRatingRow extends RatingRow {
  mode: RankedMode;
  placed: boolean;
}

function mapRankedRow(mode: RankedMode, data: any): RankedRatingRow {
  return {
    mode,
    placed: data.games >= RANKED_PLACEMENT_GAMES,
    agentId: data.agent_id,
    displayName: data.display_name,
    rating: { mu: data.mu, sigma: data.sigma },
    displayRating: data.rating,
    games: data.games,
    wins: data.wins,
    podiums: data.podiums,
    bestPlace: data.best_place,
    peak: data.peak_rating,
    placementPct: data.place_score == null ? null : data.games ? Math.round((100 * data.place_score) / data.games) : 0,
    lifetimeStats: data.lifetime_stats ?? null,
  };
}

// One agent's row on one ladder. null = no row yet (never played that mode),
// or no backend.
export async function fetchRankedRating(mode: RankedMode, agentId: string): Promise<RankedRatingRow | null> {
  if (!supabase) return null;
  try {
    const { data, error } = (await supabase
      .from("ranked_ratings")
      .select("agent_id, display_name, mu, sigma, rating, games, wins, podiums, best_place, peak_rating, place_score, lifetime_stats")
      .eq("mode", mode)
      .eq("agent_id", agentId)
      .maybeSingle()) as { data: any; error: { code?: string } | null };
    if (error || !data) return null;
    return mapRankedRow(mode, data);
  } catch {
    return null;
  }
}

// Both ladders for one agent, in one call. A missing ladder is null.
export async function fetchRankedRatings(agentId: string): Promise<Record<RankedMode, RankedRatingRow | null> | null> {
  if (!supabase) return null;
  try {
    const { data, error } = (await supabase
      .from("ranked_ratings")
      .select("mode, agent_id, display_name, mu, sigma, rating, games, wins, podiums, best_place, peak_rating, place_score, lifetime_stats")
      .eq("agent_id", agentId)) as { data: any[] | null; error: { code?: string } | null };
    if (error) return null;
    const out: Record<RankedMode, RankedRatingRow | null> = { duel: null, ffa: null };
    for (const d of data || []) if (d.mode === "duel" || d.mode === "ffa") out[d.mode as RankedMode] = mapRankedRow(d.mode, d);
    return out;
  } catch {
    return null;
  }
}

// A ranked match history row carries its ladder.
export interface RankedMatchHistoryRow extends MatchHistoryRow {
  mode: RankedMode;
}

// One agent's latest ranked matches, oldest first; all ladders unless `mode`
// is given.
export async function fetchRankedMatchHistory(agentId: string, mode?: RankedMode, limit = 300): Promise<RankedMatchHistoryRow[] | null> {
  if (!supabase) return null;
  try {
    let q = supabase.from("ranked_match_results").select("*").eq("agent_id", agentId);
    if (mode) q = q.eq("mode", mode);
    const { data, error } = await q.order("created_at", { ascending: false }).limit(limit);
    if (error) return null;
    return (data || []).reverse().map((d: any) => ({
      mode: d.mode,
      placement: d.placement,
      fieldSize: d.field_size,
      map: d.map ?? null,
      playstyle: d.playstyle ?? null,
      muBefore: d.mu_before,
      muAfter: d.mu_after,
      createdAt: d.created_at,
      matchId: d.match_id ?? null,
    }));
  } catch {
    return null;
  }
}

// Everything achievements judge from a player's history, casual and ranked:
// every match ever (achievements are for keeps), so paged past the API's row
// cap, and only the few columns they read. Oldest first. null = couldn't load.
export interface AchievementHistoryRow {
  ranked: RankedMode | null; // null = casual
  placement: number;
  fieldSize: number;
  muBefore: number;
  muAfter: number;
  createdAt: string;
  stats?: any; // the match's fun stats for the player (rounds won, gaps, power-ups...)
}
const ACH_PAGE = 1000;
const ACH_MAX_PAGES = 20; // 20k matches a table: far past anyone, and bounded

async function fetchAllRows(table: string, cols: string, agentId: string): Promise<any[] | null> {
  const out: any[] = [];
  for (let page = 0; page < ACH_MAX_PAGES; page++) {
    const { data, error } = await supabase!
      .from(table)
      .select(cols)
      .eq("agent_id", agentId)
      .order("created_at", { ascending: true })
      .range(page * ACH_PAGE, page * ACH_PAGE + ACH_PAGE - 1);
    // a column the database doesn't have yet (stats: migration 0006): without it
    if (error && cols.includes(", stats") && String(error.message || "").includes("stats")) return fetchAllRows(table, cols.replace(", stats", ""), agentId);
    if (error) return null;
    out.push(...(data || []));
    if (!data || data.length < ACH_PAGE) break;
  }
  return out;
}

export async function fetchAchievementHistory(agentId: string): Promise<AchievementHistoryRow[] | null> {
  if (!supabase) return null;
  try {
    const cols = "placement, field_size, mu_before, mu_after, created_at, stats";
    const [casual, ranked] = await Promise.all([
      fetchAllRows("casual_match_results", cols, agentId),
      // the ranked tables may be missing on an older database: no ranked rows then
      fetchAllRows("ranked_match_results", cols + ", mode", agentId).then((r) => r ?? []),
    ]);
    if (!casual) return null;
    const map = (d: any, mode: RankedMode | null): AchievementHistoryRow => ({
      ranked: mode, placement: d.placement, fieldSize: d.field_size, muBefore: d.mu_before, muAfter: d.mu_after, createdAt: d.created_at, stats: d.stats ?? null,
    });
    return [
      ...casual.map((d) => map(d, null)),
      ...ranked.filter((d) => d.mode === "duel" || d.mode === "ffa").map((d) => map(d, d.mode)),
    ].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  } catch {
    return null;
  }
}

// A player's achievements as the server keeps them (migration 0015): progress
// counters and the level reached in each. undefined = no row yet (the server
// hasn't seen them play since achievements launched), null = couldn't load
// (or the table isn't there yet). Either way the profile falls back to
// working it out from the match history.
export interface PlayerAchievementsRow {
  progress: any;
  levels: Record<string, number>;
  backfilled: Record<string, number>;
}
export async function fetchPlayerAchievements(agentId: string): Promise<PlayerAchievementsRow | null | undefined> {
  if (!supabase) return null;
  try {
    const { data, error } = await supabase.from("player_achievements").select("progress, levels, backfilled").eq("user_id", agentId).maybeSingle();
    if (error) return null;
    return data ? { progress: data.progress || {}, levels: data.levels || {}, backfilled: data.backfilled || {} } : undefined;
  } catch {
    return null;
  }
}

// The ranked leaderboards: same shape as the casual one, one ladder per call.
const RLB_COLS = "position, agent_id, display_name, rating, games, wins, podiums, best_place, placement_pct, flag";

async function queryRankedLeaderboard(mode: RankedMode, shape: (q: any) => any): Promise<LeaderboardEntry[] | null> {
  if (!supabase) return null;
  try {
    const { data, error } = (await shape(supabase.from("ranked_leaderboard").select(RLB_COLS).eq("mode", mode))) as { data: any[] | null; error: { code?: string } | null };
    if (error) return null;
    return (data ?? []).map(mapLeaderboardRow);
  } catch {
    return null;
  }
}

// (The admin's filter, as on the casual ladder: positions stay the public ones.)
const rankedFilter = (filter: LeaderboardFilter, q: any) => (filter.humansOnly ? q.not("agent_id", "like", MASKED_BOT_PREFIX + "%") : q);

export async function fetchRankedLeaderboard(mode: RankedMode, limit = 100, filter: LeaderboardFilter = {}): Promise<LeaderboardEntry[] | null> {
  return queryRankedLeaderboard(mode, (q) => rankedFilter(filter, q).order("position", { ascending: true }).limit(limit));
}

export async function fetchRankedLeaderboardPosition(mode: RankedMode, agentId: string): Promise<number | null> {
  const mine = await fetchRankedLeaderboardEntry(mode, agentId);
  return mine ? mine.position : null;
}

// One player's row on a ranked ladder (null: not on it, or the read failed).
export async function fetchRankedLeaderboardEntry(mode: RankedMode, agentId: string): Promise<LeaderboardEntry | null> {
  const mine = await queryRankedLeaderboard(mode, (q) => q.eq("agent_id", agentId).limit(1));
  return mine && mine.length ? mine[0] : null;
}

export async function fetchRankedLeaderboardAround(mode: RankedMode, agentId: string, span = 5, filter: LeaderboardFilter = {}): Promise<LeaderboardEntry[] | null> {
  const mine = await queryRankedLeaderboard(mode, (q) => q.eq("agent_id", agentId).limit(1));
  if (!mine) return null;
  if (!mine.length) return [];
  const p = mine[0].position;
  return queryRankedLeaderboard(mode, (q) => rankedFilter(filter, q).gte("position", Math.max(1, p - span)).lte("position", p + span).order("position", { ascending: true }));
}
